import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const CustomFeeReport = () => {
  const today = new Date().toISOString().split("T")[0];
  const storedSession = localStorage.getItem("currentSessionId") || "";

  const [currentSession] = useState(storedSession);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [activeTab, setActiveTab] = useState("paid");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const printRef = useRef();
const getMonthName = (key) => {
  const map = {
    month1: "Apr", month2: "May", month3: "Jun",
    month4: "Jul", month5: "Aug", month6: "Sep",
    month7: "Oct", month8: "Nov", month9: "Dec",
    month10: "Jan", month11: "Feb", month12: "Mar"
  };
  return map[key] || "";
};
  /* ================= FETCH CLASSES ================= */
  useEffect(() => {
    if (!currentSession) return;

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses`, {
      params: { sessionId: currentSession }
    })
      .then(res => setClasses(res.data))
      .catch(console.error);
  }, [currentSession]);

  /* ================= FETCH SECTIONS ================= */
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClass)}/getSectionsByClassName`, {
      params: { sessionId: currentSession }
    })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : res.data.sections || [];
        setSections(data);
        setSelectedSection(""); // reset selection
      })
      .catch(console.error);
  }, [selectedClass, currentSession]);

  /* ================= FETCH STUDENTS ================= */
  const fetchStudents = async () => {
    if (!fromDate || !toDate) {
      alert("Please select a valid date range");
      return;
    }

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/fees/report/customreport/students`, {
        params: {
          sessionId: currentSession,
          fromDate,
          toDate,
          classId: selectedClass || undefined,
          sectionId: selectedSection || undefined
        }
      });

      // Map missing sections if backend lacks field
      const dataWithSection = res.data.map(s => {
        if (!s.section && s.className && s.className.includes("-")) {
          const parts = s.className.split("-");
          return { ...s, className: parts[0], section: parts[1] };
        }
        return s;
      });

      setStudents(dataWithSection);
    } catch (err) {
      console.error(err);
      alert("Error fetching report");
    }
  };

  /* ================= FILTER STUDENTS ================= */
const filteredStudents = useMemo(() => {
  return students.filter(s => {

    const matchesSearch =
      `${s.firstName} ${s.lastName || ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesSection = selectedSection
      ? s.section === selectedSection
      : true;

    const hasCash = (s.entries || []).some(e => e.paymentType === "cash");
    const hasCheque = (s.entries || []).some(e => e.paymentType === "cheque");

    // ✅ PAYMENT FILTER
    let matchesPayment = true;

   if (paymentFilter === "cash") {
  matchesPayment = hasCash && (s.cashAmount || 0) > 0;
} else if (paymentFilter === "cheque") {
  matchesPayment = hasCheque && (s.chequeAmount || 0) > 0;
}

    // ✅ EXISTING TABS
    if (activeTab === "paid") {
      return (s.paidAmount || 0) > 0 && matchesSearch && matchesSection && matchesPayment;
    }

    if (activeTab === "unpaid") {
      return (s.balance || 0) > 0 && matchesSearch && matchesSection && matchesPayment;
    }

    return false;

  });
}, [students, searchTerm, activeTab, selectedSection, paymentFilter]);

  /* ================= TOTALS ================= */
const totals = useMemo(() => {
  let totalPaid = 0;
  let totalPending = 0;
  let totalCash = 0;
  let totalCheque = 0;

  filteredStudents.forEach((s) => {
    totalPaid += Number(s.paidAmount) || 0;
    totalPending += Number(s.balance) || 0;
    totalCash += Number(s.cashAmount) || 0;
    totalCheque += Number(s.chequeAmount) || 0;
  });

  return { totalPaid, totalPending, totalCash, totalCheque };
}, [filteredStudents]);

  /* ================= EXPORT ================= */
const exportToExcel = () => {

  if (!filteredStudents.length) return;

  const dynamicHeads = new Set();

  // ✅ get dynamic heads from entries
  filteredStudents.forEach(s => {
    (s.entries || []).forEach(entry => {
      (entry.paymentDetails?.feeHeads || []).forEach(fee => {
        const name = fee.feeHeadName;

        if (
          !name.startsWith("month") &&
          !name.startsWith("route_month") &&
          !name.toLowerCase().includes("opening")
        ) {
          dynamicHeads.add(name);
        }
      });
    });
  });

  const dynamicColumns = Array.from(dynamicHeads);

  const totals = {
    monthly: 0,
    route: 0,
    paid: 0
  };

  dynamicColumns.forEach(h => totals[h] = 0);

  const rows = [];

  // ✅ MAIN LOOP (student → entries)
  filteredStudents.forEach((s) => {

    (s.entries || []).forEach((entry) => {

      // ❌ skip zero payment entries
      if (!entry.amount || entry.amount === 0) return;

      let monthly = 0;
      let route = 0;

      const dynamicValues = {};
      dynamicColumns.forEach(h => dynamicValues[h] = 0);

      let tuitionMonths = [];
      let routeMonths = [];

      // ✅ FIXED SOURCE HERE
      (entry.paymentDetails?.feeHeads || []).forEach((fee) => {

        const name = fee.feeHeadName;
        const paid = fee.amountPaid || 0;

        // ❌ skip zero fee
        if (paid === 0) return;

        // ❌ skip opening
        if (name?.toLowerCase().includes("opening")) return;

        if (name.startsWith("month")) {
          monthly += paid;
          tuitionMonths.push(name);
        }

        else if (name.startsWith("route_month")) {
          route += paid;
          routeMonths.push(name.replace("route_", ""));
        }

        else if (dynamicColumns.includes(name)) {
          dynamicValues[name] += paid;
        }

      });

      totals.monthly += monthly;
      totals.route += route;
      totals.paid += entry.amount || 0;

      dynamicColumns.forEach(h => {
        totals[h] += dynamicValues[h];
      });

      // sort months
      tuitionMonths = [...new Set(tuitionMonths)].sort((a,b)=>Number(a.replace("month",""))-Number(b.replace("month","")));
      routeMonths = [...new Set(routeMonths)].sort((a,b)=>Number(a.replace("month",""))-Number(b.replace("month","")));

      const tuitionRange = tuitionMonths.length
        ? `${getMonthName(tuitionMonths[0])}-${getMonthName(tuitionMonths[tuitionMonths.length - 1])}`
        : "";

      const routeRange = routeMonths.length
        ? `${getMonthName(routeMonths[0])}-${getMonthName(routeMonths[routeMonths.length - 1])}`
        : "";

      rows.push({
        "Receipt No": entry.receiptNumber || "",
        "Date": entry.date ? new Date(entry.date).toISOString().split("T")[0] : "",

        "Student": `${s.firstName || ""} ${s.lastName || ""}`.trim(),
        "Father": s.fatherName || "",
        "Class": s.className || "",

        ...dynamicValues,

        "Tuition Fee": monthly,
        "Tuition Months": tuitionRange,

        "Van Fee": route,
        "Van Months": routeRange,

        "Amount Paid": entry.amount || 0,

        "Payment Mode":
          entry.paymentType === "cash"
            ? "Cash"
            : entry.paymentType === "cheque"
            ? "Cheque"
            : ""
      });

    });

  });

  // ✅ TOTAL ROW
  const totalRow = {
    "Receipt No": "",
    "Date": "",
    "Student": "TOTAL",
    "Father": "",
    "Class": ""
  };

  dynamicColumns.forEach(h => {
    totalRow[h] = totals[h];
  });

  totalRow["Tuition Fee"] = totals.monthly;
  totalRow["Van Fee"] = totals.route;
  totalRow["Amount Paid"] = totals.paid;

 
rows.sort((a, b) => {
  const getNumber = (val) => {
    if (!val) return 0;

    const num = String(val).match(/\d+/);
    return num ? Number(num[0]) : 0;
  };

  return getNumber(a["Receipt No"]) - getNumber(b["Receipt No"]);
});
 rows.push(totalRow);
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);

  ws["!cols"] = [
    { wch: 20 },
    { wch: 25 },
    { wch: 20 },
    { wch: 20 },
    { wch: 10 },

    ...dynamicColumns.map(() => ({ wch: 14 })),

    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 }
  ];

  XLSX.utils.book_append_sheet(wb, ws, "Fee Report");
  XLSX.writeFile(wb, "Fee_Report.xlsx");
};
  /* ================= PRINT ================= */
  const handlePrint = () => {
    const newWindow = window.open("", "", "width=900,height=650");
    newWindow.document.write(`
      <html>
        <head>
          <title>Custom Fee Report</title>
          <style>
            body { font-family: Arial, sans-serif; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
            th { background-color: #f3f4f6; }
          </style>
        </head>
        <body>
          <h2 style="text-align:center;">Custom Fee Report</h2>
          ${printRef.current.innerHTML}
        </body>
      </html>
    `);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-xl p-6">

        <h2 className="text-2xl font-bold mb-6 text-gray-700">Custom Fee Report</h2>

        {/* SUMMARY */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-green-100 p-4 rounded">
            <h4 className="text-green-700 font-semibold">Total Paid</h4>
            <p className="text-2xl font-bold text-green-800">₹{totals.totalPaid}</p>
          </div>
          <div className="bg-blue-100 p-4 rounded">
            <h4 className="text-blue-700 font-semibold">Total Cash</h4>
            <p className="text-2xl font-bold text-blue-800">₹{totals.totalCash}</p>
          </div>
          <div className="bg-yellow-100 p-4 rounded">
            <h4 className="text-yellow-700 font-semibold">Total Cheque</h4>
            <p className="text-2xl font-bold text-yellow-800">₹{totals.totalCheque}</p>
          </div>
          <div className="bg-red-100 p-4 rounded">
            <h4 className="text-red-700 font-semibold">Total Pending</h4>
            <p className="text-2xl font-bold text-red-800">₹{totals.totalPending}</p>
          </div>
        </div>

        {/* FILTERS */}
        <div className="grid md:grid-cols-6 gap-4 mb-6">
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border rounded px-3 py-2"/>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border rounded px-3 py-2"/>
          <select
  value={paymentFilter}
  onChange={(e) => setPaymentFilter(e.target.value)}
  className="border rounded px-3 py-2"
>
  <option value="all">All Payments</option>
  <option value="cash">Cash</option>
  <option value="cheque">Cheque</option>
</select>
          <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} className="border rounded px-3 py-2">
            <option value="">Select Class</option>
            {classes.map(c => <option key={c._id} value={c.className}>{c.className}</option>)}
          </select>
          <select value={selectedSection} onChange={e => setSelectedSection(e.target.value)} className="border rounded px-3 py-2" disabled={!selectedClass}>
            <option value="">Select Section</option>
            {sections.map(sec => <option key={sec._id} value={sec.sectionName}>{sec.sectionName}</option>)}
          </select>
          <input type="text" placeholder="Search student..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="border rounded px-3 py-2"/>
          <button onClick={fetchStudents} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Search</button>
        </div>

        {/* TABS */}
        <div className="flex space-x-4 mb-4">
          <button onClick={() => setActiveTab("paid")} className={`px-4 py-2 rounded ${activeTab==="paid"?"bg-green-600 text-white":"bg-gray-200"}`}>Paid</button>
          <button onClick={() => setActiveTab("unpaid")} className={`px-4 py-2 rounded ${activeTab==="unpaid"?"bg-red-600 text-white":"bg-gray-200"}`}>Pending</button>
        </div>

        {/* ACTIONS */}
        <div className="flex justify-end gap-4 mb-4">
          <button onClick={exportToExcel} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">Export Excel</button>
          <button onClick={handlePrint} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Print</button>
        </div>

        {/* TABLE */}
        <div ref={printRef} className="overflow-x-auto">
          <table className="min-w-full border text-center">
            <thead className="bg-gray-200">
              <tr>
                <th className="border px-2 py-2">Sr</th>
                <th className="border px-2 py-2">Name</th>
                <th className="border px-2 py-2">Class</th>
                <th className="border px-2 py-2">Section</th>
                <th className="border px-2 py-2">Paid</th>
                <th className="border px-2 py-2">Cash</th>
                <th className="border px-2 py-2">Cheque</th>
                <th className="border px-2 py-2">Pending</th>
                <th className="border px-2 py-2">{activeTab==="paid"?"Payment Date":"-"}</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length ? filteredStudents.map((s,i)=>(
                <tr key={i}>
                  <td className="border px-2 py-2">{i+1}</td>
                  <td className="border px-2 py-2">{s.firstName} {s.lastName || ""}</td>
                  <td className="border px-2 py-2">{s.className}</td>
                  <td className="border px-2 py-2">{s.section || "N/A"}</td>
                  <td className="border px-2 py-2 text-green-700 font-semibold">₹{s.paidAmount || 0}</td>
                  <td className="border px-2 py-2">₹{s.cashAmount || 0}</td>
                  <td className="border px-2 py-2">₹{s.chequeAmount || 0}</td>
                  <td className="border px-2 py-2 text-red-700 font-semibold">₹{s.balance || 0}</td>
                  <td className="border px-2 py-2">{activeTab==="paid" ? s.date || "—" : "-"}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="9" className="py-6 text-gray-500">No records found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomFeeReport;